import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { adminApi } from '../api/examApi';
import { facultyApi } from '../api/facultyApi';
import { DashboardLayout } from '../components/layout';
import ReadOnlyScheduleGrid from '../components/schedule/ReadOnlyScheduleGrid';

const CAMPUS_OPTIONS = [
  { value: 'ardee', label: 'Ardee City' },
  { value: 'arcadia', label: 'Arcadia' },
];

const DAY_OPTIONS = [
  { value: 'mon', label: 'Monday' },
  { value: 'tue', label: 'Tuesday' },
  { value: 'wed', label: 'Wednesday' },
  { value: 'thu', label: 'Thursday' },
  { value: 'fri', label: 'Friday' },
  { value: 'sat', label: 'Saturday' },
  { value: 'sun', label: 'Sunday' },
];

const selectClass =
  'rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool';

export default function AdminSchedulePage() {
  const [faculty, setFaculty] = useState([]);
  const [facultyId, setFacultyId] = useState('');
  const [groups, setGroups] = useState([]);

  const [campus, setCampus] = useState('');
  const [dayOfWeek, setDayOfWeek] = useState('');
  const [groupId, setGroupId] = useState('');

  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    facultyApi
      .getAll({ page_size: 100 })
      .then(({ data }) => setFaculty(data.data))
      .catch(() => toast.error('Failed to load faculty'));
    adminApi
      .listGroups()
      .then(({ data }) => setGroups(data))
      .catch(() => toast.error('Failed to load groups'));
  }, []);

  const load = useCallback(async () => {
    if (!facultyId) {
      setBlocks([]);
      return;
    }
    setLoading(true);
    try {
      const params = { faculty_id: facultyId };
      if (campus) params.campus = campus;
      if (dayOfWeek) params.day_of_week = dayOfWeek;
      if (groupId) params.group_id = groupId;
      const { data } = await adminApi.getScheduleBlocks(params);
      setBlocks(data);
    } catch {
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  }, [facultyId, campus, dayOfWeek, groupId]);

  useEffect(() => {
    load();
  }, [load]);

  const selectedFaculty = faculty.find((f) => f.id === facultyId);

  return (
    <DashboardLayout title="Faculty Schedules">
      <div className="animate-fade-in space-y-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-slate-900 dark:text-white">
            Faculty Schedules
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Read-only view of each faculty's standing weekly timetable — creating, editing, and
            deleting classes stays on the faculty schedule page.
          </p>
        </div>

        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
          <div className="flex flex-wrap items-center gap-4">
            <select
              value={facultyId}
              onChange={(e) => setFacultyId(e.target.value)}
              className={selectClass}
              data-testid="schedule-faculty-select"
            >
              <option value="">Select faculty…</option>
              {faculty.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>

            <select
              value={campus}
              onChange={(e) => setCampus(e.target.value)}
              className={selectClass}
              data-testid="schedule-campus-filter"
            >
              <option value="">All campuses</option>
              {CAMPUS_OPTIONS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>

            <select
              value={dayOfWeek}
              onChange={(e) => setDayOfWeek(e.target.value)}
              className={selectClass}
              data-testid="schedule-day-filter"
            >
              <option value="">All days</option>
              {DAY_OPTIONS.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>

            <select
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
              className={selectClass}
              data-testid="schedule-group-filter"
            >
              <option value="">All groups</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>

            {selectedFaculty && !loading && (
              <p className="text-sm text-slate-500 dark:text-slate-400 ml-auto">
                {blocks.length} class{blocks.length === 1 ? '' : 'es'} for {selectedFaculty.name}
              </p>
            )}
          </div>
        </div>

        {loading ? (
          <div className="animate-pulse h-96 rounded-lg bg-slate-100 dark:bg-slate-800" />
        ) : !facultyId ? (
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-16 text-center text-slate-400 text-sm">
            Select a faculty member to view their weekly schedule.
          </div>
        ) : (
          <ReadOnlyScheduleGrid
            blocks={blocks}
            emptyMessage={`No standing classes ${
              campus || dayOfWeek || groupId ? 'match these filters' : `scheduled for ${selectedFaculty?.name || 'this faculty'}`
            }.`}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
